import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

function PlatformSettings() {
  const [settings, setSettings] = useState({
    conversion_assist_enabled: true,
    conversion_assist_strength: 2,
    autopilot_outbound_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await api.getPlatformSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load platform settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load platform settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        conversion_assist_enabled: settings.conversion_assist_enabled,
        conversion_assist_strength: settings.conversion_assist_strength,
        autopilot_outbound_enabled: settings.autopilot_outbound_enabled,
      };
      const data = await api.updatePlatformSettings(payload);
      setSettings(data);
      setMessage({ type: 'success', text: 'Platform settings saved.' });
    } catch (error) {
      console.error('Failed to save platform settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save platform settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading platform settings...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🧭 Platform</h1>
        <p style={styles.subtitle}>Global settings (super admin only)</p>
      </div>

      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
          color: message.type === 'success' ? '#86efac' : '#fca5a5',
        }}>
          {message.text}
        </div>
      )}

      <div style={styles.card}>
        {/* Autopilot */}
        <div style={{ ...styles.cardHeader, marginBottom: 18 }}>
          <div>
            <h2 style={styles.cardTitle}>Follow-Up Agent Outbound</h2>
            <p style={styles.cardSubtitle}>Global kill-switch for sending SMS/email from Follow-Up Agent (Agent Mode) (staging-safe).</p>
          </div>
          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={!!settings.autopilot_outbound_enabled}
              onChange={(e) => setSettings({
                ...settings,
                autopilot_outbound_enabled: e.target.checked,
              })}
            />
            <span style={styles.toggleLabel}>
              {settings.autopilot_outbound_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>

        <div style={{ height: 1, background: '#334155', margin: '8px 0 18px' }} />

        {/* Conversion Assist */}
        <div style={styles.cardHeader}>
          <div>
            <h2 style={styles.cardTitle}>Conversion Assist</h2>
            <p style={styles.cardSubtitle}>Controls the global CD tuning for chat conversion.</p>
          </div>
          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={!!settings.conversion_assist_enabled}
              onChange={(e) => setSettings({
                ...settings,
                conversion_assist_enabled: e.target.checked,
              })}
            />
            <span style={styles.toggleLabel}>
              {settings.conversion_assist_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>

        <div style={{ ...styles.sliderSection, opacity: settings.conversion_assist_enabled ? 1 : 0.5 }}>
          <div style={styles.sliderHeader}>
            <span style={styles.sliderLabel}>Strength</span>
            <span style={styles.sliderValue}>{settings.conversion_assist_strength}</span>
          </div>
          <input
            type="range"
            min="0"
            max="3"
            step="1"
            value={settings.conversion_assist_strength}
            onChange={(e) => setSettings({
              ...settings,
              conversion_assist_strength: Number(e.target.value),
            })}
            style={styles.slider}
            disabled={!settings.conversion_assist_enabled}
          />
          <div style={styles.sliderTicks}>
            {[0, 1, 2, 3].map((tick) => (
              <span key={tick} style={styles.sliderTick}>{tick}</span>
            ))}
          </div>
        </div>

        <div style={styles.actions}>
          <button
            style={styles.saveButton}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#0f172a',
    color: '#f8fafc',
    padding: '32px',
  },
  loading: {
    padding: '32px',
    color: '#94a3b8',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    margin: 0,
  },
  subtitle: {
    marginTop: '8px',
    color: '#94a3b8',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '10px',
    marginBottom: '20px',
  },
  card: {
    background: '#1e293b',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
    border: '1px solid #334155',
    maxWidth: '720px',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  cardTitle: {
    margin: 0,
    fontSize: '20px',
  },
  cardSubtitle: {
    margin: '6px 0 0',
    color: '#94a3b8',
    fontSize: '14px',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#0f172a',
    padding: '8px 12px',
    borderRadius: '999px',
    border: '1px solid #334155',
  },
  toggleLabel: {
    fontSize: '14px',
  },
  sliderSection: {
    marginTop: '24px',
  },
  sliderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  sliderLabel: {
    fontSize: '14px',
    color: '#cbd5f5',
  },
  sliderValue: {
    fontSize: '18px',
    fontWeight: '600',
  },
  slider: {
    width: '100%',
  },
  sliderTicks: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '6px',
    color: '#94a3b8',
    fontSize: '12px',
  },
  sliderTick: {
    width: '24px',
    textAlign: 'center',
  },
  actions: {
    marginTop: '24px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  saveButton: {
    background: '#38bdf8',
    color: '#0f172a',
    padding: '10px 18px',
    borderRadius: '10px',
    border: 'none',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default PlatformSettings;
